import React from 'react';
import { PageHeader, Table, Tag } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import type { TableColumnsType } from 'antd';
import { formatDateTime } from '../../../utils/dateTimeUtil';
import VehicleStatus from '../../../core/enum/VehicleStatus';
import VehicleStatusColorCode from '../../../core/enum/VehicleStatusColorCode';
import {
  getVehicleColors,
  getVehicleMakes,
  getVehicleModels,
} from '../../../app/store/commonSlice';
import { useSelector } from 'react-redux';
import { convertToCurrencyString } from '../../../utils/currencyUtil';
import { useGetAuctionByIdQuery } from '../../../app/api/auctionApiSlice';
import BackButton from '../../../components/common/BackButton';
import AuctionItem from '../../../core/models/AuctionItem';
import { Bid } from '../../../core/models/Bid';
import Vehicle from '../../../core/models/Vehicle';

const ListAuctionVehicles: React.FC = () => {
  const navigate = useNavigate();

  const vehicleMakes = useSelector(getVehicleMakes);
  const vehicleModels = useSelector(getVehicleModels);
  const vehicleColors = useSelector(getVehicleColors);

  const { auctionId } = useParams<{ auctionId: any }>();

  const { data: auction, isLoading } = useGetAuctionByIdQuery(auctionId);

  const statusFilter = Object.values(VehicleStatus)
    .filter((value) => isNaN(Number(value)))
    .map((status) => ({
      text: status.toString(),
      value: status.toString(),
    }));

  const columns: ColumnsType<AuctionItem> = [
    {
      title: 'Make',
      dataIndex: ['vehicle', 'make'],
      key: 'make',
      render: (value: number) => vehicleMakes?.find((make) => make.id === value)?.name,
      sorter: (a, b) => a.vehicle.make - b.vehicle.make,
      filters: vehicleMakes?.map((make) => ({ text: make.name, value: make.id })),
      filterSearch: true,
      onFilter: (value, record) => {
        return record.vehicle.make === value;
      },
      responsive: ['md'],
    },
    {
      title: 'Model',
      dataIndex: ['vehicle', 'model'],
      key: 'model',
      render: (value: number) => vehicleModels?.find((model) => model.id === value)?.name,
      sorter: (a, b) => a.vehicle.model - b.vehicle.model,
      filters: vehicleModels?.map((model) => ({ text: model.name, value: model.id })),
      filterSearch: true,
      onFilter: (value, record) => {
        return record.vehicle.model === value;
      },
      responsive: ['md'],
    },
    {
      title: 'YoM',
      dataIndex: ['vehicle', 'yom'],
      key: 'yom',
      sorter: (a, b) => a.vehicle.yom - b.vehicle.yom,
      render: (text: string) => text,
      onFilter: (value, record) => {
        return record.vehicle.yom.toString().toLowerCase().includes(value.toString().toLowerCase());
      },
      responsive: ['md'],
    },
    {
      title: 'Mileage',
      dataIndex: ['vehicle', 'mileage'],
      key: 'mileage',
      sorter: (a, b) => a.vehicle.mileage - b.vehicle.mileage,
      render: (mileage: number) => <>{mileage?.toLocaleString()} km</>,
      onFilter: (value, record) => {
        return record.vehicle.mileage
          .toString()
          .toLowerCase()
          .includes(value.toString().toLowerCase());
      },
      responsive: ['xl'],
    },
    {
      title: 'Color',
      dataIndex: ['vehicle', 'color'],
      key: 'color',
      render: (value: number) => vehicleColors?.find((color) => color.id === value)?.name,
      sorter: (a, b) => a.vehicle.color - b.vehicle.color,
      filters: vehicleColors?.map((color) => ({ text: color.name, value: color.id })),
      filterSearch: true,
      onFilter: (value, record) => {
        return record.vehicle.color === value;
      },
      responsive: ['xl'],
    },
    {
      //   title: 'Auction Status',
      //   dataIndex: ['status'],
      //   key: 'auctionItemStatus',
      //   render: (status: AuctionItemStatus) => (
      //     <Tag color={AuctionItemStatusColorCode[status]}>{AuctionItemStatus[status]}</Tag>
      //   ),
      //   sorter: (a, b) => a.status - b.status,
      //   filters: statusFilter,
      //   onFilter: (value, record) => {
      //     return AuctionItemStatus[record.status] === value;
      //   },
      // },
      // {
      //   title: 'Current Status',
      title: 'Status',
      dataIndex: ['vehicle', 'status'],
      key: 'status',
      render: (status: VehicleStatus) => (
        <Tag color={VehicleStatusColorCode[status]}>{VehicleStatus[status]}</Tag>
      ),
      sorter: (a, b) => a.vehicle.status - b.vehicle.status,
      filters: statusFilter,
      onFilter: (value, record) => {
        return VehicleStatus[record.vehicle.status] === value;
      },
    },
    {
      title: 'Reserve Price',
      dataIndex: 'reservePrice',
      key: 'reservePrice',
      sorter: (a, b) => a.reservePrice - b.reservePrice,
      render: (value: number) => convertToCurrencyString(value),
      onFilter: (value, record) => {
        return record.reservePrice
          .toString()
          .toLowerCase()
          .includes(value.toString().toLowerCase());
      },
      responsive: ['md'],
    },
    {
      title: 'Sold Price',
      dataIndex: ['vehicle', 'purchase', 'soldPrice'],
      key: 'soldPrice',
      sorter: (a, b) =>
        (a.vehicle.purchase ? a.vehicle.purchase.soldPrice : 0) -
        (b.vehicle.purchase ? b.vehicle.purchase.soldPrice : 0),
      render: (value: number) => {
        return value ? convertToCurrencyString(value) : '-';
      },
      responsive: ['md'],
    },
    {
      title: 'Buyer',
      dataIndex: ['vehicle', 'purchase', 'buyer'],
      key: 'buyer',
      sorter: (a, b) =>
        (a.vehicle?.purchase?.buyer ? a.vehicle?.purchase?.buyer : '-').localeCompare(
          b.vehicle?.purchase?.buyer ? b.vehicle?.purchase?.buyer : '-',
        ),
      render: (value: string) => {
        return value || '-';
      },
      responsive: ['md'],
    },
    {
      title: 'Highest Bid',
      dataIndex: ['vehicle', 'currentBid', 'bidPrice'],
      key: 'bidPrice',
      sorter: (a, b) =>
        (a.vehicle.currentBid ? a.vehicle.currentBid.bidPrice : 0) -
        (b.vehicle.currentBid ? b.vehicle.currentBid.bidPrice : 0),
      render: (value: number) => {
        return value ? convertToCurrencyString(value) : '-';
      },
      responsive: ['md'],
    },
    {
      title: 'Seller',
      dataIndex: ['vehicle'],
      key: 'seller',
      sorter: (a, b) =>
        (a.vehicle?.seller?.companyName || a.vehicle?.seller?.personInCharge).localeCompare(
          b.vehicle?.seller?.companyName || b.vehicle?.seller?.personInCharge,
        ),
      render: (value: Vehicle) => {
        return value?.seller?.companyName || value?.seller?.personInCharge;
      },
      responsive: ['md'],
    },
  ];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const expandedRowRender = (record: AuctionItem) => {
    const columns: TableColumnsType<Bid> = [
      { title: 'Buyer', dataIndex: ['buyer', 'name'], key: 'buyer' },
      {
        title: 'Bid',
        dataIndex: 'bidPrice',
        key: 'bidPrice',
        render: (amount: number) => convertToCurrencyString(amount),
      },
    ];

    const data = record.bids ?? [];

    return <Table columns={columns} dataSource={data} pagination={false} size='small' />;
  };

  return (
    <>
      <BackButton />
      <PageHeader
        className='site-page-header'
        title={auction?.title}
        subTitle={formatDateTime(auction?.auctionDate)}
      />
      <Table
        sortDirections={['descend', 'ascend']}
        dataSource={auction?.auctionItems ?? []}
        columns={columns}
        rowKey='id'
        loading={isLoading}
        size='small'
        expandable={{ expandedRowRender, defaultExpandedRowKeys: ['0'] }}
        onRow={(record) => {
          return {
            onClick: () => {
              navigate(`/vehicles/${record.vehicleId}`);
            },
          };
        }}
      />
    </>
  );
};

export default ListAuctionVehicles;
